import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  SharedGantt,
  SharedTree,
  ViewToggle,
  type SnapshotResponse,
  type ViewMode,
} from "../components/sharedTaskRender";

// Compact, chrome-less read-only task list for embedding in an Excalidraw
// web-embed box (route: /embed/:token).  It reuses the same snapshot
// endpoint and renderers as the public SharedView page, but strips the
// page header/footer/legend so it fills the iframe tightly.
//
// IMPORTANT — opaque-origin safety: an Excalidraw embeddable iframe is
// sandboxed.  For our own URLs ExcalidrawView's renderEmbeddable keeps the
// frame same-origin (allow-same-origin), but as a defensive measure this
// component must NOT touch localStorage/sessionStorage on mount — accessing
// storage in an opaque (null) origin throws a SecurityError and would blank
// the embed.  Hence the view toggle is in-memory only (no persistence).

type LoadState =
  | { status: "loading" }
  | { status: "ok"; data: SnapshotResponse }
  | { status: "error"; message: string };

export function EmbedView() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [view, setView] = useState<ViewMode>("tree");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ status: "error", message: "Missing token in URL." });
      return;
    }
    (async () => {
      try {
        const res = await fetch(`/api/shared/${encodeURIComponent(token)}`);
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as
            | { error?: string }
            | null;
          const msg =
            res.status === 401
              ? "This link is invalid or has expired."
              : res.status === 403
                ? "This link has been revoked."
                : body?.error ?? `HTTP ${res.status}`;
          if (!cancelled) setState({ status: "error", message: msg });
          return;
        }
        const data = (await res.json()) as SnapshotResponse;
        if (!cancelled) setState({ status: "ok", data });
      } catch (err) {
        if (!cancelled)
          setState({
            status: "error",
            message: err instanceof Error ? err.message : String(err),
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === "loading") {
    return (
      <div className="flex h-full min-h-[120px] items-center justify-center bg-white p-4 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-1 bg-white p-4 text-center dark:bg-slate-900">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          Can't open this list
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{state.message}</p>
      </div>
    );
  }

  const data = state.data;
  const title =
    data.scope === "group"
      ? data.group?.name ?? "Shared group"
      : data.rootItem?.title || "Shared item";

  return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-900">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <h1 className="truncate text-sm font-semibold tracking-tight text-slate-800 dark:text-slate-100">
          {title}
        </h1>
        <ViewToggle view={view} onView={setView} />
      </header>

      <main className="flex-1 overflow-auto p-3">
        {data.items.length === 0 ? (
          <p className="text-sm italic text-slate-500 dark:text-slate-400">Nothing to show.</p>
        ) : view === "tree" ? (
          <SharedTree data={data} />
        ) : (
          <SharedGantt data={data} />
        )}
      </main>
    </div>
  );
}
